import { useState } from 'react';
import { http } from '../lib/api';
import { Alert, Button, CopyButton } from './core';

/**
 * Fetch the publishing credential only when its owner explicitly requests it.
 * The server URL is shared by every stream; the key is what makes each stream
 * unique, so once revealed we also show the combined full ingest URL.
 */
export function StreamKeyField({ streamId, last4, serverUrl }: { streamId: string; last4: string; serverUrl?: string }) {
  const [streamKey, setStreamKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reveal = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await http.get<{ streamKey: string }>(`/api/tenant/streams/${streamId}/key`);
      if (!result.streamKey) throw new Error('Stream key is unavailable. Please try again.');
      setStreamKey(result.streamKey);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const fullUrl = streamKey && serverUrl ? `${serverUrl.replace(/\/+$/, '')}/${streamKey}` : null;

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      <div className="flex items-center gap-2">
        <code className="text-sm flex-1" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          {streamKey ?? `••••••••${last4 || ''}`}
        </code>
        {streamKey ? (
          <>
            <CopyButton text={streamKey} />
            <Button variant="secondary" size="sm" onClick={() => setStreamKey(null)}>Hide key</Button>
          </>
        ) : (
          <Button variant="secondary" size="sm" disabled={loading} onClick={() => void reveal()}>
            {loading ? 'Loading…' : 'Show key'}
          </Button>
        )}
      </div>
      {streamKey && fullUrl ? (
        <div className="flex items-center gap-2 mt-2">
          <code className="text-sm flex-1" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {fullUrl}
          </code>
          <CopyButton text={fullUrl} />
        </div>
      ) : null}
      {error && <Alert kind="danger">{error}</Alert>}
      <div className="text-muted text-xs mt-2">Keep this key private. It is unique per stream — two streams never share a path.</div>
    </div>
  );
}
